import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

import {isInstantDelivery, isFastDelivery} from '@stubhub/delivery-utils';
import {determineIsTrader} from '@stubhub/trader-helpers';

import {getVFSImageUrl, getListingRow, getListingSeats, getListingId} from '../../../../helpers/listing-helper';
import InfiniteScroll from '../../components/react-infinite-scroll';
import TopModule from '../../components/react-top-module';

import RoyalTicketListPanel from './components/react-ticket-list-panel';
import {ZONE_COLOR_OPACITY_HEX} from './constants';

import './index.scss';

const DEFAULT_DEBOUNCE_TIME = 150;

class RoyalTicketList extends React.PureComponent {
  static contextTypes = {
    globalRegistry: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      ticketListScrolling: false,
    };
  }

  componentDidMount() {
    if (__CLIENT__ && window && window.performance && window.performance.getEntriesByName) {
      const markTicketListInventory = window.performance.getEntriesByName('mark-ticket-list-ready');

      const hasMarkTicketListInventory = markTicketListInventory && markTicketListInventory.length === 1;

      if (!hasMarkTicketListInventory) {
        if (window.performance.mark) {
          window.performance.mark('mark-ticket-list-ready');
        }
      }
    }
  }

  handleScrollFunctions = () => {
    this.handleScrolling();
    this.handleNotScrolling();
  };

  handleScrolling = debounce(
    () => {
      this.setState({ticketListScrolling: true});
    },
    DEFAULT_DEBOUNCE_TIME,
    {
      leading: true,
      trailing: false,
    }
  );

  handleNotScrolling = debounce(() => {
    this.setState({ticketListScrolling: false});
  }, DEFAULT_DEBOUNCE_TIME);

  render() {
    const {
      className = '',
      listings = [],
      hasMore,
      setPage,
      page,
      venueId,
      venueConfigId,
      deviceType,
      showTicketRecoInd,
      handleSelectTicket,
      viewFromSection,
      isGA,
      partner,
      buyerMessages,
      showBrokerInfo,
      showBrokerInfoDSA,
      handleHoveredListingSectionId,
      isEventInfoLoading,
      listingLoading,
      showImportantInfoModal,
      zoneColors = [],
      eventCountry,
      eventCurrency,
    } = this.props;

    const isEventGA = Boolean(isGA);

    const {ticketListScrolling = false} = this.state;

    const listingsPanel = listings.reduce((result, listing, index) => {
      const {
        isGA: isListingGA,
        sectionName,
        sectionId,
        price = {},
        seats = [],
        valuePercentage,
        deliveryTypeList = [],
        listingAttributeList,
        listingAttributeListWithCategories,
        sellerType,
        splitVector = [],
        sellerOwnInd,
        zoneId,
        isCma,
        isDsa,
        isDsaAccepted,
        expectedInhandDate,
      } = listing;

      const isInstantDeliveryPresent = isInstantDelivery(deliveryTypeList);
      const isFastDeliveryPresent = isFastDelivery(deliveryTypeList, expectedInhandDate);

      const srs = {
        section: sectionName,

        /*
         * TODO Get row and seatnumber More Info GCG-692
         * row: '22',
         */
        row: !isListingGA && getListingRow(seats),
        // Seats: '7-9'
        seats: !isListingGA && getListingSeats(seats),
      };

      const listingId = getListingId(seats);
      const vfsImage = getVFSImageUrl({
        venueId,
        venueConfigId,
        sectionId,
        isThumb: true,
      });

      const isTrader = determineIsTrader(
        showBrokerInfoDSA,
        showBrokerInfo,
        isCma,
        isDsa,
        isDsaAccepted,
        sellerType,
        eventCountry,
        eventCurrency
      );

      const zoneColor = zoneColors && zoneColors[zoneId] ? `${zoneColors[zoneId]}${ZONE_COLOR_OPACITY_HEX}` : null;

      result.push(
        <RoyalTicketListPanel
          ticketRank={index + 1}
          key={`${listingId}-${index}`}
          listingId={listingId}
          index={index}
          sectionName={sectionName}
          seats={seats}
          srs={srs}
          splitVector={splitVector}
          isListingGA={Boolean(isListingGA)}
          isEventGA={isEventGA}
          image={vfsImage}
          price={price}
          viewFromSection={viewFromSection}
          handleSelectTicket={handleSelectTicket(listing)}
          showTicketRecoInd={showTicketRecoInd}
          valuePercentage={valuePercentage}
          isInstantDelivery={isInstantDeliveryPresent}
          isFastDelivery={isFastDeliveryPresent}
          partner={partner}
          isTrader={isTrader}
          listingAttributeList={listingAttributeList}
          listingAttributeListWithCategories={listingAttributeListWithCategories}
          showBrokerInfo={showBrokerInfo}
          ticketListScrolling={ticketListScrolling}
          sectionId={sectionId}
          handleHoveredListingSectionId={handleHoveredListingSectionId}
          sellerOwnInd={sellerOwnInd}
          eventLoading={isEventInfoLoading}
          listingLoading={listingLoading}
          zoneId={zoneId}
          zoneColor={zoneColor}
          deliveryTypeList={deliveryTypeList}
          expectedInhandDate={expectedInhandDate}
        />
      );

      return result;
    }, []);

    return (
      <>
        {deviceType === 'desktop' && <TopModule disclaimerMsg={showImportantInfoModal ? null : buyerMessages} />}
        <div className={`RoyalTicketList__container ${className}`}>
          <InfiniteScroll
            loadMore={setPage}
            page={page}
            initialLoad={false}
            hasMore={hasMore}
            onScroll={this.handleScrollFunctions}
          >
            <ul className={`RoyalTicketList__container ${className}`}>{listingsPanel}</ul>
          </InfiniteScroll>
        </div>
      </>
    );
  }
}

RoyalTicketList.propTypes = {
  /**
   * Array of listings
   */
  listings: PropTypes.array,

  /**
   * If there are more listings to show
   */
  hasMore: PropTypes.bool,
  setPage: PropTypes.func,

  /**
   * Method to handle hovered map section
   */
  handleHoveredListingSectionId: PropTypes.func,

  /**
   * The Class Name used to override the Styling for the component.
   */
  className: PropTypes.string,
  page: PropTypes.number,

  /**
   * Venue ID
   */
  venueId: PropTypes.number,

  /**
   * Venue config object ID
   */
  venueConfigId: PropTypes.number,

  /**
   * Device typ
   */
  deviceType: PropTypes.string,

  /**
   * If ticket reco is shown
   */
  showTicketRecoInd: PropTypes.bool,

  /**
   * Function to handle ticket selection
   */
  handleSelectTicket: PropTypes.func.isRequired,

  /**
   * If view from section is available
   */
  viewFromSection: PropTypes.bool,
  isGA: PropTypes.bool,
  partner: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Buyer messages
   */
  buyerMessages: PropTypes.array,

  /**
   * If broker information can be shown
   */
  showBrokerInfo: PropTypes.bool,

  /**
   * If broker information can be shown in DSA
   */
  showBrokerInfoDSA: PropTypes.bool,

  /**
   * If event info is loading
   */
  isEventInfoLoading: PropTypes.bool,

  /**
   * If listing is loading
   */
  listingLoading: PropTypes.bool,

  /**
   * If important messages can be shown
   */
  showImportantInfoModal: PropTypes.bool,

  /**
   * Mapping of zones and colors
   */
  zoneColors: PropTypes.object,

  /**
   * Event country code
   */
  eventCountry: PropTypes.string,

  /**
   * Event currency code
   */
  eventCurrency: PropTypes.string,
};

export default RoyalTicketList;
